import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { useTenant } from '@/contexts/TenantContext';
import { toast } from 'sonner';
import {
  FileText, Download, Send, Settings2, RefreshCw, Loader2,
  ChevronDown, Filter, Mail, Building2, BadgeCheck, Euro,
  CalendarDays, User, Archive
} from 'lucide-react';

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
                 "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function statusBadge(status) {
  const map = {
    confirmed: { label: 'Confermata', color: '#22c55e' },
    pending:   { label: 'In attesa',  color: '#f59e0b' },
    cancelled: { label: 'Annullata',  color: '#ef4444' },
    completed: { label: 'Completata', color: '#3b82f6' },
  };
  const s = map[status] || { label: status, color: '#888' };
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
      style={{ background: `${s.color}20`, color: s.color }}>
      {s.label}
    </span>
  );
}

// ── Tab: Ricevute ─────────────────────────────────────────────────────────────
function ReceiptsTab() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [receipts, setReceipts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/receipts', { params: { month, year } });
      setReceipts(res.data.receipts);
      setTotal(res.data.total);
    } catch {
      toast.error('Errore nel caricamento delle ricevute');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (id, num) => {
    setDownloading(id);
    try {
      const res = await api.get(`/receipts/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ricevuta_${num}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Errore nel download della ricevuta');
    } finally {
      setDownloading(null);
    }
  };

  const downloadReport = async () => {
    setGenerating(true);
    try {
      const res = await api.get('/receipts/report/pdf', {
        params: { month, year },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${MONTHS[month - 1].toLowerCase()}_${year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report scaricato');
    } catch {
      toast.error('Errore nella generazione del report');
    } finally {
      setGenerating(false);
    }
  };

  const sendToAccountant = async () => {
    setSending(true);
    try {
      await api.post('/receipts/send-report', null, { params: { month, year } });
      toast.success('Report inviato al commercialista');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio email. Verifica le impostazioni.');
    } finally {
      setSending(false);
    }
  };

  const downloadArchive = async (scope) => {
    // scope: 'month' | 'year'
    setArchiving(true);
    try {
      const params = scope === 'month' ? { year, month } : { year };
      const res = await api.get('/receipts/archive/zip', {
        params,
        responseType: 'blob',
        timeout: 120000,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const label = scope === 'month' ? `${MONTHS[month - 1].toLowerCase()}_${year}` : `anno_${year}`;
      a.download = `archivio_ricevute_${label}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(scope === 'month'
        ? `Archivio ${MONTHS[month - 1]} ${year} scaricato`
        : `Archivio annuale ${year} scaricato`);
    } catch {
      toast.error("Errore nella generazione dell'archivio");
    } finally {
      setArchiving(false);
    }
  };

  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <div className="space-y-5" data-testid="receipts-tab">
      {/* Filtri + azioni report */}
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg"
        style={{ background: 'var(--lux-surface-1)', border: '1px solid var(--lux-border-1)' }}>
        <div className="flex items-center gap-2">
          <Filter size={14} style={{ color: 'var(--lux-gold)' }} />
          <span className="text-sm font-medium text-white">Periodo:</span>
        </div>

        <select
          data-testid="receipts-month-select"
          value={month}
          onChange={e => setMonth(Number(e.target.value))}
          className="px-3 py-1.5 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white focus:outline-none focus:border-[var(--lux-gold)]">
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>

        <select
          data-testid="receipts-year-select"
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="px-3 py-1.5 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white focus:outline-none focus:border-[var(--lux-gold)]">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <button onClick={load}
          className="p-1.5 rounded border transition-colors"
          style={{ borderColor: 'var(--lux-border-4)', color: 'var(--lux-text-muted-2)' }}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>

        <div className="flex-1" />

        {/* Totale periodo */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded"
          style={{ background: 'rgba(201,168,76,0.1)' }}>
          <Euro size={13} style={{ color: 'var(--lux-gold)' }} />
          <span className="text-sm font-medium" style={{ color: 'var(--lux-gold)' }}>
            {MONTHS[month - 1]} {year}: <strong>€{total.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</strong>
          </span>
        </div>

        <button
          data-testid="receipts-download-report-btn"
          onClick={downloadReport}
          disabled={generating || receipts.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-opacity disabled:opacity-40"
          style={{ border: '1px solid var(--lux-gold)', color: 'var(--lux-gold)' }}>
          {generating ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
          Scarica Report PDF
        </button>

        <div className="relative group">
          <button
            data-testid="receipts-archive-menu-btn"
            disabled={archiving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-opacity disabled:opacity-40"
            style={{ border: '1px solid var(--lux-gold)', color: 'var(--lux-gold)' }}>
            {archiving ? <Loader2 size={12} className="animate-spin" /> : <Archive size={12} />}
            Archivio ZIP
            <ChevronDown size={10} />
          </button>
          {!archiving && (
            <div className="absolute right-0 top-full mt-1 hidden group-hover:block z-20 min-w-[220px] rounded shadow-lg"
              style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)' }}>
              <button
                data-testid="receipts-archive-month-btn"
                onClick={() => downloadArchive('month')}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[var(--lux-surface-3)] transition-colors"
                style={{ color: 'white' }}>
                <FileText size={11} style={{ color: 'var(--lux-gold)' }} />
                Solo {MONTHS[month - 1]} {year}
              </button>
              <div style={{ borderTop: '1px solid var(--lux-border-1)' }} />
              <button
                data-testid="receipts-archive-year-btn"
                onClick={() => downloadArchive('year')}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[var(--lux-surface-3)] transition-colors"
                style={{ color: 'white' }}>
                <Archive size={11} style={{ color: 'var(--lux-gold)' }} />
                Intero anno {year}
              </button>
              <div className="px-3 py-2 text-[10px] border-t"
                style={{ color: 'var(--lux-text-muted-3)', borderColor: 'var(--lux-border-1)' }}>
                Include: PDF singoli, report mensili, CSV riepilogo
              </div>
            </div>
          )}
        </div>

        <button
          data-testid="receipts-send-report-btn"
          onClick={sendToAccountant}
          disabled={sending || receipts.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-opacity disabled:opacity-40 text-black"
          style={{ background: 'var(--lux-gold)' }}>
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          Invia al Commercialista
        </button>
      </div>

      {/* Tabella ricevute */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={22} className="animate-spin" style={{ color: 'var(--lux-gold)' }} />
        </div>
      ) : receipts.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--lux-text-muted)' }}>
          <FileText size={36} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nessuna prenotazione in {MONTHS[month - 1]} {year}</p>
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden border border-[var(--lux-border-1)]">
          <table className="w-full text-sm" data-testid="receipts-table">
            <thead style={{ background: 'var(--lux-surface-2)' }}>
              <tr>
                {['N° Ricevuta', 'Data', 'Ospite', 'Proprietà', 'Check-in', 'Check-out', 'Importo', 'Stato', 'Azioni'].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs font-medium"
                    style={{ color: 'var(--lux-text-muted-3)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {receipts.map((r, i) => (
                <tr key={r.id}
                  style={{
                    background: i % 2 === 0 ? 'var(--lux-surface-1)' : 'var(--lux-bg)',
                    borderTop: '1px solid var(--lux-border-1)',
                  }}>
                  <td className="px-3 py-2.5 font-mono text-xs" style={{ color: 'var(--lux-gold)' }}>{r.receipt_number}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--lux-text-muted-2)' }}>{r.created_at}</td>
                  <td className="px-3 py-2.5">
                    <div className="text-white text-xs font-medium">{r.guest_name}</div>
                    <div className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>{r.guest_email}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-white">{r.property_name}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--lux-text-muted-2)' }}>{r.checkin}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--lux-text-muted-2)' }}>{r.checkout}</td>
                  <td className="px-3 py-2.5 text-xs font-medium" style={{ color: 'var(--lux-gold)' }}>
                    €{r.total_price.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2.5">{statusBadge(r.status)}</td>
                  <td className="px-3 py-2.5">
                    <button
                      data-testid={`receipts-download-${r.id}`}
                      onClick={() => downloadPdf(r.id, r.receipt_number)}
                      disabled={downloading === r.id}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-opacity disabled:opacity-40"
                      style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text-muted-2)' }}>
                      {downloading === r.id ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab: Impostazioni Commercialista ──────────────────────────────────────────
function SettingsTab() {
  const [form, setForm] = useState({
    company_name: '', vat_number: '', address: '', city: '', cap: '',
    country: 'Italia', accountant_email: '', accountant_name: '', auto_send_monthly: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoStatus, setAutoStatus] = useState(null);
  const [autoLog, setAutoLog] = useState([]);
  const [sendingTest, setSendingTest] = useState(false);

  const loadAutoStatus = async () => {
    try {
      const [statusRes, logRes] = await Promise.all([
        api.get('/receipts/auto-send-status'),
        api.get('/receipts/auto-send-log'),
      ]);
      setAutoStatus(statusRes.data);
      setAutoLog(logRes.data.log || []);
    } catch {
      // no-op
    }
  };

  useEffect(() => {
    api.get('/receipts/settings').then(res => {
      const s = res.data.settings || {};
      setForm(prev => ({
        ...prev,
        company_name: s.company_name || '',
        vat_number: s.vat_number || '',
        address: s.address || '',
        city: s.city || '',
        cap: s.cap || '',
        country: s.country || 'Italia',
        accountant_email: s.accountant_email || '',
        accountant_name: s.accountant_name || '',
        auto_send_monthly: !!s.auto_send_monthly,
      }));
    }).catch(() => {}).finally(() => setLoading(false));
    loadAutoStatus();
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/receipts/settings', form);
      toast.success('Impostazioni salvate');
      await loadAutoStatus();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={22} className="animate-spin" style={{ color: 'var(--lux-gold)' }} />
    </div>
  );

  const inp = "w-full px-3 py-2 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white placeholder:text-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)]";

  return (
    <div className="space-y-5 max-w-2xl" data-testid="receipts-settings-tab">
      {/* Dati aziendali */}
      <div className="card-dark rounded-lg p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-[var(--lux-border-1)]">
          <Building2 size={15} style={{ color: 'var(--lux-gold)' }} />
          <span className="text-sm font-medium text-white">Dati Aziendali (intestazione ricevute)</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5">
            <label className="text-xs font-medium text-white">Ragione Sociale</label>
            <input data-testid="receipts-company-name" type="text" value={form.company_name}
              onChange={e => set('company_name', e.target.value)}
              placeholder="ShangriLux Hotels S.r.l." className={inp} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-white">Partita IVA</label>
            <input data-testid="receipts-vat" type="text" value={form.vat_number}
              onChange={e => set('vat_number', e.target.value)}
              placeholder="IT12345678901" className={inp} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-white">Paese</label>
            <input type="text" value={form.country}
              onChange={e => set('country', e.target.value)} placeholder="Italia" className={inp} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <label className="text-xs font-medium text-white">Indirizzo</label>
            <input type="text" value={form.address}
              onChange={e => set('address', e.target.value)} placeholder="Via Roma 1" className={inp} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-white">Città</label>
            <input type="text" value={form.city}
              onChange={e => set('city', e.target.value)} placeholder="Milano" className={inp} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-white">CAP</label>
            <input type="text" value={form.cap}
              onChange={e => set('cap', e.target.value)} placeholder="20100" className={inp} />
          </div>
        </div>
      </div>

      {/* Dati commercialista */}
      <div className="card-dark rounded-lg p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-[var(--lux-border-1)]">
          <Mail size={15} style={{ color: 'var(--lux-gold)' }} />
          <span className="text-sm font-medium text-white">Dati Commercialista</span>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-white">Nome Commercialista</label>
          <input data-testid="receipts-accountant-name" type="text" value={form.accountant_name}
            onChange={e => set('accountant_name', e.target.value)}
            placeholder="Dott. Mario Rossi" className={inp} />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-white">Email Commercialista</label>
          <input data-testid="receipts-accountant-email" type="email" value={form.accountant_email}
            onChange={e => set('accountant_email', e.target.value)}
            placeholder="commercialista@studio.it" className={inp} />
          <p className="text-xs mt-1" style={{ color: 'var(--lux-text-muted-3)' }}>
            Questo indirizzo riceverà i report mensili quando clicchi &quot;Invia al Commercialista&quot;.
          </p>
        </div>
      </div>

      {/* Invio Automatico Mensile */}
      <div className="card-dark rounded-lg p-5 space-y-4" data-testid="receipts-autosend-card">
        <div className="flex items-center gap-2 pb-2 border-b border-[var(--lux-border-1)]">
          <Archive size={15} style={{ color: 'var(--lux-gold)' }} />
          <span className="text-sm font-medium text-white">Invio Automatico Archivio ZIP Mensile</span>
          <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-widest font-medium"
            style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--lux-gold)' }}>Nuovo</span>
        </div>

        <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>
          Il 1° di ogni mese, alle 06:00 UTC, il sistema genera automaticamente lo ZIP con tutte le ricevute
          del mese precedente e lo invia direttamente al commercialista. Zero click, archiviazione fiscale garantita.
        </p>

        {/* Toggle */}
        <div className="flex items-center justify-between p-3 rounded"
          style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-1)' }}>
          <div>
            <div className="text-sm font-medium text-white">Attiva invio automatico</div>
            <div className="text-[11px] mt-0.5" style={{ color: 'var(--lux-text-muted-3)' }}>
              Richiede email commercialista configurata
            </div>
          </div>
          <button
            type="button"
            data-testid="receipts-autosend-toggle"
            onClick={() => set('auto_send_monthly', !form.auto_send_monthly)}
            className="relative w-11 h-6 rounded-full transition-colors"
            style={{ background: form.auto_send_monthly ? 'var(--lux-gold)' : 'var(--lux-border-4)' }}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.auto_send_monthly ? 'translate-x-5' : ''}`} />
          </button>
        </div>

        {/* Status scheduler */}
        {autoStatus && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="px-3 py-2 rounded" style={{ background: 'var(--lux-surface-2)' }}>
              <div style={{ color: 'var(--lux-text-muted-3)' }} className="text-[10px] uppercase tracking-widest mb-1">Scheduler</div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full"
                  style={{ background: autoStatus.scheduler_running ? '#22c55e' : '#ef4444' }} />
                <span style={{ color: 'white' }}>
                  {autoStatus.scheduler_running ? 'Attivo' : 'Non attivo'}
                </span>
              </div>
            </div>
            <div className="px-3 py-2 rounded" style={{ background: 'var(--lux-surface-2)' }}>
              <div style={{ color: 'var(--lux-text-muted-3)' }} className="text-[10px] uppercase tracking-widest mb-1">Prossimo invio</div>
              <div style={{ color: 'var(--lux-gold)' }} className="font-medium">
                {autoStatus.next_run_at
                  ? new Date(autoStatus.next_run_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : '—'}
              </div>
            </div>
          </div>
        )}

        {/* Test send now */}
        <div className="flex items-center gap-2 pt-1">
          <button
            data-testid="receipts-test-send-archive-btn"
            onClick={async () => {
              if (!form.accountant_email) { toast.error('Configura prima l\'email del commercialista'); return; }
              setSendingTest(true);
              try {
                const now = new Date();
                // mese precedente
                const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                await api.post('/receipts/send-archive', null, {
                  params: { month: d.getMonth() + 1, year: d.getFullYear() },
                  timeout: 120000,
                });
                toast.success('Test inviato al commercialista');
                await loadAutoStatus();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Errore invio archivio');
              } finally {
                setSendingTest(false);
              }
            }}
            disabled={sendingTest || !form.accountant_email}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-opacity disabled:opacity-40"
            style={{ border: '1px solid var(--lux-gold)', color: 'var(--lux-gold)' }}>
            {sendingTest ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Invia archivio mese scorso ora (test)
          </button>
        </div>

        {/* Log ultimi invii */}
        {autoLog.length > 0 && (
          <div className="pt-3 border-t border-[var(--lux-border-1)]">
            <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted-3)' }}>
              Ultimi invii
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="receipts-autosend-log">
              {autoLog.slice(0, 6).map((entry, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] px-2 py-1.5 rounded"
                  style={{ background: 'var(--lux-surface-2)' }}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: entry.success ? '#22c55e' : '#ef4444' }} />
                  <span style={{ color: 'white' }} className="font-mono">
                    {new Date(entry.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                  </span>
                  <span style={{ color: 'var(--lux-text-muted-2)' }}>
                    {String(entry.month).padStart(2, '0')}/{entry.year}
                  </span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-widest"
                    style={{
                      background: entry.trigger === 'auto_cron' ? 'rgba(201,168,76,0.15)' : 'var(--lux-surface-3)',
                      color: entry.trigger === 'auto_cron' ? 'var(--lux-gold)' : 'var(--lux-text-muted-2)',
                    }}>
                    {entry.trigger === 'auto_cron' ? 'Auto' : 'Manuale'}
                  </span>
                  <div className="flex-1" />
                  {entry.success
                    ? <span style={{ color: 'var(--lux-text-muted-2)' }}>{entry.bookings_count || 0} ricevute · € {(entry.total_amount || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                    : <span style={{ color: '#ef4444' }}>{entry.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <button
        data-testid="receipts-settings-save-btn"
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 rounded text-sm font-medium text-black transition-opacity disabled:opacity-50"
        style={{ background: 'var(--lux-gold)' }}>
        {saving ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />}
        {saving ? 'Salvataggio...' : 'Salva impostazioni'}
      </button>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export function ReceiptsPage() {
  const [tab, setTab] = useState('receipts');

  const tabs = [
    { id: 'receipts', label: 'Ricevute', icon: FileText },
    { id: 'settings', label: 'Impostazioni', icon: Settings2 },
  ];

  return (
    <div data-testid="receipts-page" className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <FileText size={18} style={{ color: 'var(--lux-gold)' }} />
          <h1 className="text-white text-xl font-light font-serif">Ricevute & CRM</h1>
        </div>
        <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>
          Gestisci ricevute, genera report PDF mensili e inviali al commercialista.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-lg w-fit" style={{ background: 'var(--lux-surface-2)' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`receipts-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className="flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-all"
            style={{
              background: tab === t.id ? 'var(--lux-surface-3)' : 'transparent',
              color: tab === t.id ? 'white' : 'var(--lux-text-muted-2)',
              border: tab === t.id ? '1px solid var(--lux-border-4)' : '1px solid transparent',
            }}>
            <t.icon size={13} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'receipts' ? <ReceiptsTab /> : <SettingsTab />}
    </div>
  );
}
